import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  myAccountDetailsApi,
  doctorAddPatientMemberApi,
  doctorEditPhoneNumberApi,
  fetchAllMedicalCategoryApi,
  listAntecedentConfigsApi,
  fetchAntecedentTitles,
  fetchAntecedentOptionsByTitle,
  fetchAllAllergens,
  fetchAllergensByGroup,
  listDiagnosticsApi,
  getDiagnosticTitlesApi,
  saveDiagnosticApi,
  deleteDiagnosticApi,
  AddIndicatorApi,
  getIndicatorApi,
  deleteIndicatorApi,
  getIndicatorCategoryApi,
  addMedicalBackgroundApi,
  deleteMedicalBackgroundApi,
} from '../services/patientService';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch patient profile details
 */
export function usePatientDetail(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.detail(patientId),
    queryFn: async ({ signal }) => {
      const response = await myAccountDetailsApi({ patientId, signal });
      return response?.data?.data ?? response?.data ?? null;
    },
    enabled: Boolean(patientId),
    ...options,
  });
}

/**
 * Default initial categories structure for medical background widget
 */
export const DEFAULT_MEDICAL_BACKGROUND_CATEGORIES = [
  { id: 1, name: 'Antécédents', sub_category: [] },
  { id: 2, name: 'Mode de vie', sub_category: [] },
  { id: 3, name: 'Allergies', sub_category: [] },
  { id: 4, name: 'Traitement en cours', sub_category: [] },
];

/**
 * Direct async fetcher helper for patient medical background categories & items
 */
export async function fetchPatientMedicalBackgroundQuery({ patientId, doctorId, appointmentId, signal }) {
  if (!patientId || !doctorId) return DEFAULT_MEDICAL_BACKGROUND_CATEGORIES;
  const params = {
    patient_id: patientId,
    doctor_id: doctorId,
  };
  if (appointmentId) params.appointment_id = appointmentId;

  const response = await fetchAllMedicalCategoryApi(params, { signal });
  if (response?.data?.res === true && Array.isArray(response?.data?.data)) {
    return response.data.data;
  }
  return Array.isArray(response?.data?.data)
    ? response.data.data
    : (Array.isArray(response?.data) ? response.data : DEFAULT_MEDICAL_BACKGROUND_CATEGORIES);
}

/**
 * Fetch patient medical background (antecedents, lifestyle, allergies, treatments)
 * Cached for ~5 minutes to persist across active appointment sessions without refetch noise.
 */
export function usePatientMedicalBackground(patientId, doctorId, appointmentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.medicalBackground(patientId, doctorId, appointmentId),
    queryFn: ({ signal }) => fetchPatientMedicalBackgroundQuery({ patientId, doctorId, appointmentId, signal }),
    enabled: Boolean(patientId && doctorId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch medical categories for antecedents
 */
export function useMedicalCategories(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.categories(),
    queryFn: async () => {
      const response = await fetchAllMedicalCategoryApi();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 60 * 60 * 1000, // 1 hour for master categories
    ...options,
  });
}

/**
 * Fetch antecedent configuration list
 */
export function useAntecedentConfigs(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.antecedents(),
    queryFn: async () => {
      const response = await listAntecedentConfigsApi();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch antecedent titles
 */
export function useAntecedentTitles(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.antecedentTitles(),
    queryFn: async () => {
      const response = await fetchAntecedentTitles();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch antecedent options for a given title
 */
export function useAntecedentOptionsByTitle(title, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.antecedentOptions(title),
    queryFn: async () => {
      const response = await fetchAntecedentOptionsByTitle(title);
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(title),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch allergens catalog
 */
export function useAllergens(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.allergies(),
    queryFn: async () => {
      const response = await fetchAllAllergens();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 60 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch allergens filtered by group
 */
export function useAllergensByGroup(group, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.allergensByGroup(group),
    queryFn: async () => {
      const response = await fetchAllergensByGroup(group);
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(group),
    staleTime: 60 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch diagnostics master list
 */
export function useDiagnostics(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.diagnostics(),
    queryFn: async () => {
      const response = await listDiagnosticsApi();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch patient indicators (weight, BP, vitals)
 */
export function usePatientIndicators(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.indicators(patientId),
    queryFn: async () => {
      const response = await getIndicatorApi({ patient_id: patientId });
      return response?.data?.data ?? response?.data ?? [];
    },
    enabled: Boolean(patientId),
    ...options,
  });
}

/**
 * Fetch indicator categories
 */
export function useIndicatorCategories(options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.indicatorCategories(),
    queryFn: async () => {
      const response = await getIndicatorCategoryApi();
      return response?.data?.data ?? response?.data ?? [];
    },
    staleTime: 60 * 60 * 1000,
    ...options,
  });
}

// ============================================================================
// Mutation Hooks (with Targeted Invalidation & Optimistic Updates)
// ============================================================================

/**
 * Add a member to a patient family/account
 */
export function useAddPatientMember(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => doctorAddPatientMemberApi(variables),
    onSuccess: (data, variables) => {
      const patientId = variables?.patient_id;
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.patients.members(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.patients.detail(patientId),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Optimistically update patient phone number
 */
export function useEditPhoneNumber(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => doctorEditPhoneNumberApi(variables),
    onMutate: async (variables) => {
      const patientId = variables?.patient_id || variables?.id;
      if (!patientId) return {};

      const queryKey = queryKeys.patients.detail(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousPatient = queryClient.getQueryData(queryKey);

      if (previousPatient) {
        queryClient.setQueryData(queryKey, (old) => ({
          ...old,
          phone: variables.phone ?? variables.phone_number ?? old.phone,
        }));
      }

      return { previousPatient, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousPatient && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousPatient);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistically add an indicator (e.g. blood pressure, glucose)
 */
export function useAddIndicator(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => AddIndicatorApi(variables),
    onMutate: async (variables) => {
      const patientId = variables?.patient_id;
      if (!patientId) return {};

      const queryKey = queryKeys.patients.indicators(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousIndicators = queryClient.getQueryData(queryKey) || [];
      const optimisticItem = {
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        is_pending: true,
        ...variables,
      };

      queryClient.setQueryData(queryKey, (old = []) => {
        return Array.isArray(old) ? [optimisticItem, ...old] : [optimisticItem];
      });

      return { previousIndicators, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousIndicators && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousIndicators);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.patients.indicators(context.patientId) });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistically delete an indicator
 */
export function useDeleteIndicator(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => deleteIndicatorApi(variables),
    onMutate: async (variables) => {
      const patientId = variables?.patient_id;
      const indicatorId = variables?.id || variables?.indicator_id;
      if (!patientId) return {};

      const queryKey = queryKeys.patients.indicators(patientId);
      await queryClient.cancelQueries({ queryKey });

      const previousIndicators = queryClient.getQueryData(queryKey) || [];

      queryClient.setQueryData(queryKey, (old = []) => {
        if (!Array.isArray(old)) return old;
        return old.filter((item) => item.id !== indicatorId);
      });

      return { previousIndicators, queryKey, patientId };
    },
    onError: (err, variables, context) => {
      if (context?.previousIndicators && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousIndicators);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.patientId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.patients.indicators(context.patientId) });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Add medical background (antecedents) with targeted invalidation
 */
export function useAddMedicalBackground(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => addMedicalBackgroundApi(variables),
    onSuccess: (data, variables) => {
      const patientId = variables?.get?.('patient_id') || variables?.patient_id;
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: ['patients', 'medical-background', String(patientId)],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.patients.detail(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.observations.timeline(patientId),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Delete medical background (antecedents) with targeted invalidation
 */
export function useDeleteMedicalBackground(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => deleteMedicalBackgroundApi(variables),
    onSuccess: (data, variables) => {
      const patientId = variables?.get?.('patient_id') || variables?.patient_id;
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: ['patients', 'medical-background', String(patientId)],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.patients.detail(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.observations.timeline(patientId),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Fetch diagnostic titles for a doctor
 */
export function useDiagnosticTitles(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.patients.diagnostics(),
    queryFn: async () => {
      const norm = (s) =>
        String(s || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim();

      const extractArray = (resp) => {
        const root = resp?.data ?? resp;
        const candidates = [
          root?.data,
          root?.diagnostics,
          root?.items,
          root?.rows,
          Array.isArray(root) ? root : null,
        ].filter(Boolean);

        for (const arr of candidates) {
          if (Array.isArray(arr)) return arr;
        }
        return [];
      };

      const mapToTitle = (d) =>
        String(
          typeof d === "string"
            ? d
            : (d?.item ??
              d?.title ??
              d?.diagnostic ??
              d?.name ??
              d?.titre ??
              d?.label ??
              d?.nom ??
              d?.parametre ??
              ""),
        ).trim();

      const all = [];

      // 1) New API
      if (doctorId !== undefined && doctorId !== null) {
        try {
          const respNew = await listDiagnosticsApi(doctorId);
          const arrNew = extractArray(respNew).map(mapToTitle).filter(Boolean);
          all.push(...arrNew);
        } catch (e) {
          console.warn("[Diag] listDiagnosticsApi error:", e?.response?.data || e);
        }
      }

      // 2) Old API (merge/fallback)
      try {
        const respOld = await getDiagnosticTitlesApi();
        const arrOld = extractArray(respOld)
          .map((x) =>
            typeof x === "string" ? x : (x?.titre ?? x?.title ?? x?.name),
          )
          .filter(Boolean);
        all.push(...arrOld);
      } catch (e) {
        console.warn("[Diag] getDiagnosticTitlesApi error:", e?.response?.data || e);
      }

      // deduplication
      const seen = new Set();
      const unique = [];
      for (const t of all) {
        const k = norm(t);
        if (k && !seen.has(k)) {
          seen.add(k);
          unique.push(t);
        }
      }

      return unique;
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    ...options,
  });
}

/**
 * Save (create/update) a diagnostic title
 */
export function useSaveDiagnostic(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ doctorId, payload }) => saveDiagnosticApi(doctorId, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.patients.diagnostics(),
      });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}

/**
 * Delete a diagnostic title
 */
export function useDeleteDiagnostic(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ doctorId, id }) => deleteDiagnosticApi(doctorId, id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.patients.diagnostics(),
      });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}
